import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ArticleService {
  constructor(
    private http: HttpClient,
    @Inject('$state') private $state: any,
  ) {}

  find(id: string | number): Promise<any> {
    return firstValueFrom(this.http.get<any>(`/api/v1/articles/${id}`))
      .then(body => ({ data: this.addItemUrl(body) }));
  }

  feed(parameters: any = {}): Promise<any> {
    const filter = {
      state: 'published',
      published_before: new Date().toISOString(),
      ...(parameters.filter || {}),
    };
    if (filter.state === 'review' || filter.state === 'rejected') {
      delete filter.published_before;
    }
    if (filter.user_id) {
      delete filter.published_before;
      delete filter.state;
    }
    const rawParams = {
      page: 1,
      per_page: 10,
      ...parameters,
      filter,
    };
    return firstValueFrom(
      this.http.get<any>('/api/v1/articles', { observe: 'response', params: this.buildParams(rawParams) }),
    ).then(response => ({
      data: Array.isArray(response.body) ? response.body.map((item: any) => this.addItemUrl(item)) : response.body,
      headers: (name: string) => response.headers.get(name),
    }));
  }

  like(id: number): Promise<any> {
    return firstValueFrom(this.http.post<any>(`/api/v1/articles/${id}/like`, ''))
      .then(body => ({ data: body }));
  }

  unlike(id: number): Promise<any> {
    return firstValueFrom(this.http.delete<any>(`/api/v1/articles/${id}/like`))
      .then(body => ({ data: body }));
  }

  flag(id: number, reason: string): Promise<any> {
    return firstValueFrom(this.http.post<any>(`/api/v1/articles/${id}/flag`, { reason }))
      .then(body => ({ data: body }));
  }

  create(article: any): Promise<any> {
    return firstValueFrom(this.http.post<any>('/api/v1/articles', article))
      .then(body => ({ data: body }))
      // eslint-disable-next-line prefer-promise-reject-errors
      .catch((err: HttpErrorResponse) => Promise.reject({ data: err.error }));
  }

  update(article: any): Promise<any> {
    return firstValueFrom(this.http.put<any>(`/api/v1/articles/${article.id}`, article))
      .then(body => ({ data: body }))
      // eslint-disable-next-line prefer-promise-reject-errors
      .catch((err: HttpErrorResponse) => Promise.reject({ data: err.error }));
  }

  publish(articleId: number): Promise<any> {
    return firstValueFrom(this.http.post<any>(`/api/v1/articles/${articleId}/publish`, ''))
      .then(body => ({ data: body }))
      // eslint-disable-next-line prefer-promise-reject-errors
      .catch((err: HttpErrorResponse) => Promise.reject({ data: err.error }));
  }

  review(articleId: number): Promise<any> {
    return firstValueFrom(this.http.post<any>(`/api/v1/articles/${articleId}/review`, ''))
      .then(body => ({ data: body }))
      // eslint-disable-next-line prefer-promise-reject-errors
      .catch((err: HttpErrorResponse) => Promise.reject({ data: err.error }));
  }

  reject(articleId: number, reason: string): Promise<any> {
    return firstValueFrom(this.http.post<any>(`/api/v1/articles/${articleId}/reject`, { reject_reason: reason }))
      .then(body => ({ data: body }));
  }

  draft(articleId: number): Promise<any> {
    return firstValueFrom(this.http.post<any>(`/api/v1/articles/${articleId}/draft`, ''))
      .then(body => ({ data: body }));
  }

  hide(articleId: number): Promise<any> {
    return firstValueFrom(this.http.post<any>(`/api/v1/articles/${articleId}/hide`, ''))
      .then(body => ({ data: body }));
  }

  archive(articleId: number): Promise<any> {
    return firstValueFrom(this.http.post<any>(`/api/v1/articles/${articleId}/archive`, ''))
      .then(body => ({ data: body }));
  }

  toggleHide(article: { id: number; state: string }): Promise<any> {
    return article.state === 'hidden' ? this.publish(article.id) : this.hide(article.id);
  }

  comments(articleId: number): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(`/api/v1/articles/${articleId}/comments`, { observe: 'response' }),
    ).then(response => ({
      data: response.body,
      headers: (name: string) => response.headers.get(name),
    }));
  }

  commentsPage(articleId: number, page: number, sort?: string): Promise<any> {
    const params = {
      page: page || 1,
      sort: sort || 'threaded',
    };
    return firstValueFrom(
      this.http.get<any>(`/api/v1/articles/${articleId}/comments/flat`, { observe: 'response', params }),
    ).then(response => ({
      data: response.body,
      headers: (name: string) => response.headers.get(name),
    }));
  }

  postComment(id: number, comment: any): Promise<any> {
    return firstValueFrom(this.http.post<any>(`/api/v1/articles/${id}/comments`, comment))
      .then(body => ({ data: body }));
  }

  postCommentAndReturnCommentable(id: number, comment: any): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`/api/v1/articles/${id}/comments`, { ...comment, return_commentable: true }),
    ).then(body => ({ data: body }));
  }

  remove(id: number): Promise<any> {
    return firstValueFrom(this.http.delete<any>(`/api/v1/articles/${id}`))
      .then(body => ({ data: body }));
  }

  addTodo(id: number): Promise<any> {
    return firstValueFrom(this.http.post<any>(`/api/v1/articles/${id}/todo`, ''))
      .then(body => ({ data: body }));
  }

  removeTodo(id: number): Promise<any> {
    return firstValueFrom(this.http.delete<any>(`/api/v1/articles/${id}/todo`))
      .then(body => ({ data: body }));
  }

  likedUsers(id: number, parameters: any = {}): Promise<any> {
    const params = { page: 1, per_page: 10, ...parameters };
    return firstValueFrom(
      this.http.get<any>(`/api/v1/articles/${id}/liked_users`, { observe: 'response', params }),
    ).then(response => ({
      data: response.body,
      headers: (name: string) => response.headers.get(name),
    }));
  }

  subscribe(id: number): Promise<any> {
    return firstValueFrom(this.http.post<any>(`/api/v1/articles/${id}/subscribe`, ''))
      .then(body => ({ data: body }));
  }

  unsubscribe(id: number): Promise<any> {
    return firstValueFrom(this.http.delete<any>(`/api/v1/articles/${id}/subscribe`))
      .then(body => ({ data: body }));
  }

  private buildParams(obj: any, prefix = ''): HttpParams {
    return Object.keys(obj).reduce((params, key) => {
      const paramKey = prefix ? `${prefix}[${key}]` : key;
      const val = obj[key];
      if (val === null || val === undefined) {
        return params;
      }
      if (typeof val === 'object' && !Array.isArray(val)) {
        const nested = this.buildParams(val, paramKey);
        return nested.keys().reduce((p, k) => p.set(k, nested.get(k)!), params);
      }
      return params.set(paramKey, String(val));
    }, new HttpParams());
  }

  private addItemUrl(item: any): any {
    if (item?.type === 'article') {
      item.url = this.$state.href('singlearticle', { articleId: item.slug }, { absolute: true });
    }
    return item;
  }
}
